import React, { useState } from 'react';
import { X, Inbox } from 'lucide-react';
import AttachmentPreview from './AttachmentPreview';

const NeedsAssignmentPanel = ({ messages, leads, role, onResolve, onSuggest, onClose }) => {
  const canResolve = role === 'admin' || role === 'manager';
  const [picks, setPicks] = useState({});
  const [notes, setNotes] = useState({});

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="needs-assignment-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Inbox className="w-4 h-4" /> Needs Assignment ({messages.length})
          </h3>
          <button type="button" onClick={onClose} data-testid="needs-assignment-close">
            <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <p className="text-sm text-slate-400 italic" data-testid="needs-assignment-empty">
              No tokenless inbound emails pending assignment.
            </p>
          )}
          {messages.map((m) => (
            <div key={m.id} className="border border-slate-200 rounded-xl p-3" data-testid={`unmatched-row-${m.id}`}>
              <p className="text-sm font-medium text-slate-800">{m.from_email}</p>
              <p className="text-xs text-slate-400 mb-2">
                {m.subject || '(no subject)'} · {m.reason === 'ambiguous' ? `${(m.candidate_lead_ids || []).length} possible matches` : 'no matches found'}
              </p>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2 mb-2 whitespace-pre-wrap line-clamp-3">
                {(m.body || '').slice(0, 300)}
              </p>
              {(m.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid={`unmatched-attachments-${m.id}`}>
                  {m.attachments.map((att) => <AttachmentPreview key={att.id} attachment={att} />)}
                </div>
              )}
              <div className="flex items-center gap-2">
                <select
                  value={picks[m.id] || ''}
                  onChange={(e) => setPicks((p) => ({ ...p, [m.id]: e.target.value }))}
                  data-testid={`unmatched-lead-select-${m.id}`}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                >
                  <option value="">Select a lead…</option>
                  {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.name} — {l.email}</option>)}
                </select>
                {canResolve && (
                  <button
                    type="button"
                    disabled={!picks[m.id]}
                    onClick={() => onResolve(m.id, picks[m.id])}
                    data-testid={`unmatched-resolve-button-${m.id}`}
                    className="text-sm bg-slate-900 text-white px-3 py-1.5 rounded-lg disabled:opacity-40"
                  >
                    Attach
                  </button>
                )}
              </div>
              {!canResolve && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    value={notes[m.id] || ''}
                    onChange={(e) => setNotes((n) => ({ ...n, [m.id]: e.target.value }))}
                    placeholder="Add a suggestion note…"
                    data-testid={`unmatched-suggest-input-${m.id}`}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                  />
                  <button
                    type="button"
                    disabled={!picks[m.id]}
                    onClick={() => { onSuggest(m.id, picks[m.id], notes[m.id] || ''); setNotes((n) => ({ ...n, [m.id]: '' })); }}
                    data-testid={`unmatched-suggest-button-${m.id}`}
                    className="text-sm bg-amber-100 text-amber-700 px-3 py-1.5 rounded-lg disabled:opacity-40"
                  >
                    Suggest
                  </button>
                </div>
              )}
              {(m.suggestions || []).length > 0 && (
                <div className="mt-2 space-y-1" data-testid={`unmatched-suggestions-${m.id}`}>
                  {m.suggestions.map((s, idx) => (
                    <p key={idx} className="text-[11px] text-slate-400">Suggested by {s.suggested_by}: {s.note}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NeedsAssignmentPanel;
